import { styled } from '@uhg-abyss/web/tools/styled';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { CareCategories, Constants } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { isMNRHbpbCodePresent } from '../../../../common/PSXHeader/SearchBar/utils';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useTypeaheadStore } from '../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../store/useTypeaheadStore/typeaheadStore';
import { getCurrentMember } from '../../../../utils/user.utils';
import {
  HeadingStyle,
  ResultTitleMarkdown,
  SubTitleStyle,
  TitleStyle,
  textStyle,
  titleTextStyle,
} from './SearchResultsDynamicTitle.styled';

const TitleContainer = styled('div', {
  display: 'flex',
  alignItems: 'left',
  paddingBottom: '16px',
  flexWrap: 'wrap',
  flexDirection: 'column',
  '@media (max-width: 744px)': {
    padding: '0 $md 12px',
  },
});
type SearchResultsDynamicTitlePropType = {
  choosePCP: Boolean;
  searchTitle: String;
  chipCount: number;
  onSpecialtyClick: (value: boolean, title: string) => void;
  isMixedSuggestions?: boolean;
  isNameSearchEnabled?: boolean;
  isSpecialtySearchEnabled?: boolean;
  specialtySearch: boolean;
  hasOnlyPseudoRollupCodes: boolean;
  specialtyResults?: { chipEnabled: boolean; chipCategory: string }[];
  translatedPsxKeyword?: string;
  chips: any[];
};
export const SearchResultsDynamicTitle = ({
  choosePCP,
  searchTitle,
  chipCount,
  onSpecialtyClick,
  isMixedSuggestions,
  specialtySearch,
  isNameSearchEnabled,
  isSpecialtySearchEnabled,
  hasOnlyPseudoRollupCodes,
  specialtyResults = [],
  translatedPsxKeyword = '',
  chips = [],
}: SearchResultsDynamicTitlePropType) => {
  const { t } = useTranslation();
  const [autoCompleteParallelSearchFlow, enableVisionForNonUhcMNR] =
    useFeatureFlag([
      ConstantsLagoon.FEATURE_FLAGS.AUTOCOMPLETE_PARALLEL_SEARCHFLOW,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_VISION_FOR_NON_UHC_MNR,
    ]);
  const [searchButtonResults] = useSessionStorage<any>(
    Constants.STORAGE_KEYS.SESSION.TYPE_AHEAD_SEARCH_RESULTS,
    []
  );
  const allConfigs = useLagoon('config')();
  const hbpbCodes =
    allConfigs
      ?.find((row) => row.key === 'VISION_NON_UHC_ICARE')
      ?.value?.split(',') || [];
  const { search } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      searchType: state.typeaheadSearchStore.searchType,
      includeSpecialityRollupCodes:
        state.typeaheadSearchStore.includeSpecialityRollupCodes,
      pseudoVerticals: state.typeaheadSearchStore.pseudoVerticals,
      suggestion: state.typeaheadSearchStore.suggestion,
      search: state.typeaheadSearchStore.search,
      providerType: state.typeaheadSearchStore.providerType,
      isMixedSuggestions: state.typeaheadSearchStore.isMixedSuggestions,
      isUserTextSearch: state.typeaheadSearchStore.isUserTextSearch,
    }))
  );

  let chipCountMessage = '';
  if (chipCount === 1) {
    chipCountMessage = `${chipCount} ${t('category')}`;
  } else {
    chipCountMessage = `${chipCount} ${t('categories')}`;
  }
  const data = useLagoon('ui-messaging')();
  const searchSpecialityResultTitle = find(data, {
    key: ConstantsLagoon.MIXED_SEARCH_RESULT_PAGE.SEARCH_SPECIALITY_RESULTS,
  });
  const searchNameResultTitle = find(data, {
    key: ConstantsLagoon.MIXED_SEARCH_RESULT_PAGE.SEARCH_NAME_RESULTS,
  });
  const searchSpecialityResultSubTitle = find(data, {
    key: ConstantsLagoon.MIXED_SEARCH_RESULT_PAGE
      .SEARCH_SPECIALITY_RESULTS_SUBTITLE,
  });
  const searchNameResultSubTitle = find(data, {
    key: ConstantsLagoon.MIXED_SEARCH_RESULT_PAGE.SEARCH_NAME_RESULTS_SUBTITLE,
  });
  const searchResultTitle = find(data, {
    key: ConstantsLagoon.MIXED_SEARCH_RESULT_PAGE.SEARCH_RESULT_TITLE,
  });
  const searchInsteadForHeading = find(data, {
    key: ConstantsLagoon.MIXED_SEARCH_RESULT_PAGE.SEARCH_INSTEAD_FOR_HEADING,
  });
  const searchPreferredFacilityResultSubTitle = find(data, {
    key: ConstantsLagoon.MIXED_SEARCH_RESULT_PAGE
      .SEARCH_PREFERRED_FACILITY_RESULTS_SUBTITLE,
  });
  const searchKeyWord = translatedPsxKeyword
    ? translatedPsxKeyword
    : searchTitle;
  let title = `${searchResultTitle.message.replace(
    '{searchTitle}',
    searchKeyWord
  )} ${t(chipCountMessage)}`;
  let subTitle;
  if (isMixedSuggestions && specialtySearch) {
    title = `${searchSpecialityResultTitle.message.replace(
      '{searchTitle}',
      searchKeyWord
    )} ${t(chipCountMessage)}`;
    subTitle = searchSpecialityResultSubTitle.message.replace(
      '{searchTitle}',
      searchKeyWord
    );
  }

  if (isMixedSuggestions && !specialtySearch) {
    title = `${searchNameResultTitle.message.replace(
      '{searchTitle}',
      searchKeyWord
    )} ${t(chipCountMessage)}`;
    subTitle = searchNameResultSubTitle.message.replace(
      '{searchTitle}',
      searchKeyWord
    );
  }
  if (isMixedSuggestions && !specialtySearch && hasOnlyPseudoRollupCodes) {
    title = `${searchNameResultTitle.message.replace(
      '{searchTitle}',
      searchKeyWord
    )} ${t(chipCountMessage)}`;
    subTitle = searchPreferredFacilityResultSubTitle.message.replace(
      '{searchTitle}',
      searchKeyWord
    );
  }
  const dataTestId: string = hasOnlyPseudoRollupCodes
    ? 'results-heading-link-for-preferred-facility'
    : 'results-heading-for-specialty';

  const isCategoryLinkEnabled = specialtyResults?.some(
    (item) => item?.chipEnabled
  );
  if (enableVisionForNonUhcMNR) {
    const visionChip = chips.some(
      (chip) =>
        isMNRHbpbCodePresent(getCurrentMember(), hbpbCodes) &&
        chip?.value === CareCategories.VISION &&
        searchButtonResults
          ?.find((item) => item?.suggestion === search)
          ?.coverageType?.includes('V') &&
        enableVisionForNonUhcMNR
    );
    if (visionChip) {
      title = '';
    }
  }

  return (
    <Fragment>
      {!choosePCP && searchKeyWord && (
        <Fragment>
          <TitleContainer>
            <Heading
              css={titleTextStyle}
              data-testid="result-title"
              level={1}
              size="sm"
            >
              <ResultTitleMarkdown data-testid="primary-bold-text">
                {title}
              </ResultTitleMarkdown>
            </Heading>
            {isMixedSuggestions &&
            specialtySearch &&
            (autoCompleteParallelSearchFlow === false ||
              isNameSearchEnabled) ? (
              <Text css={textStyle}>
                <TitleStyle>
                  <HeadingStyle>{searchInsteadForHeading.message}</HeadingStyle>
                  <Text
                    css={
                      {
                        'abyss-heading-root': {
                          '@media (max-width: 744px)': {
                            fontSize: 'medium !important',
                            lineHeight: '$lg !important',
                          },
                        },
                      } as any
                    }
                    data-auto-testid="results-heading-for-provider-name"
                    data-testid="results-heading-for-provider-name"
                    fontSize="sm"
                    onClick={() => onSpecialtyClick(false, 'name search')}
                  >
                    <SubTitleStyle>
                      {subTitle}
                      <IconSymbol icon="chevron_right" size="24px" />
                    </SubTitleStyle>
                  </Text>
                </TitleStyle>
              </Text>
            ) : null}
            {isMixedSuggestions &&
            !specialtySearch &&
            isCategoryLinkEnabled &&
            (autoCompleteParallelSearchFlow === false ||
              isSpecialtySearchEnabled) ? (
              <Text css={textStyle}>
                <TitleStyle>
                  <HeadingStyle>{searchInsteadForHeading.message}</HeadingStyle>
                  <Text
                    css={
                      {
                        'abyss-heading-root': {
                          '@media (max-width: 744px)': {
                            fontSize: 'medium !important',
                            lineHeight: '$lg !important',
                          },
                        },
                      } as any
                    }
                    data-auto-testid={dataTestId}
                    data-testid={dataTestId}
                    fontSize="sm"
                    onClick={() => onSpecialtyClick(true, 'specialty search')}
                  >
                    <SubTitleStyle>
                      {subTitle}
                      <IconSymbol icon="chevron_right" size="24px" />
                    </SubTitleStyle>
                  </Text>
                </TitleStyle>
              </Text>
            ) : null}
          </TitleContainer>
        </Fragment>
      )}
      {choosePCP && (
        <TitleContainer>
          <Heading
            css={{
              'abyss-heading-root': {
                '@media (min-width: 745px)': {
                  paddingBottom: '16px !important',
                  paddingTop: '8px !important',
                },
                '@media (max-width: 464px)': {
                  fontSize: 'medium !important',
                  lineHeight: '$lg !important',
                },
              },
            }}
            data-testid="provider-results-for-choose-pcp"
            level={2}
            size="sm"
            textAlign="left"
          >
            {t('Results for')} {t('primary care doctor')}
          </Heading>
        </TitleContainer>
      )}
    </Fragment>
  );
};
